import Service, { inject as service } from '@ember/service';

import type IntlService from 'ember-intl/services/intl';
import type AlertsService from 'later/services/alerts';
import type DatadogService from 'later/services/datadog';
import type ErrorsService from 'later/services/errors';
import type IntegrationsQueueService from 'later/services/integrations-queue';
import type { Integration } from 'later/services/integrations-queue';
import type PaymentService from 'later/services/payment';

const ACCEPTABLE_INTERACTION_TEXTS_FOR_PLAN_CHANGE = ['Confirm Upgrade', 'Upgrade to Scale'];

export default class AppcuesService extends Service implements Integration {
  @service declare integrationsQueue: IntegrationsQueueService;
  @service declare datadog: DatadogService;
  @service declare payment: PaymentService;
  @service declare alerts: AlertsService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;

  readonly name = 'appcues';

  get appcuesContainer(): HTMLDivElement | null {
    return document.querySelector('appcues-checklists');
  }

  setup(): void {
    if (window.Appcues) {
      // NOTE: Appcues can be active before listeners are setup. If the checklist container
      // already exists, then we need to manually addToQueue.  Polling to resolve intermitent timing issue
      // where the appcuesContainer check happens before the container gets injected into the DOM
      this.checkForAppcuesContainer();

      window.Appcues.on('checklist_shown', () => this.integrationsQueue.addToQueue(this.name));
      window.Appcues.on('checklist_skipped', () => this.integrationsQueue.removeFromQueue(this.name));
      window.Appcues.on('checklist_dismissed', () => this.integrationsQueue.removeFromQueue(this.name));
      window.Appcues.on('all', (eventName: string, eventObject: Record<string, any>) =>
        this.#handleAllEvents(eventName, eventObject)
      );
    }
  }

  checkForAppcuesContainer(): void {
    const maxPollCount = 500;
    const pollDelay = 500;
    let count = 0;
    const interval = setInterval(() => {
      if (count > maxPollCount) {
        clearInterval(interval);
      }
      if (this.appcuesContainer) {
        if (!this.integrationsQueue.isInQueue(this.name)) {
          this.integrationsQueue.addToQueue(this.name);
        }
        clearInterval(interval);
      }
      count++;
    }, pollDelay);
  }

  show(): void {
    if (this.appcuesContainer) {
      this.appcuesContainer.style.display = '';
    }
  }

  track(...args: unknown[]): void {
    window.Appcues?.track(...args);
  }

  hide(): void {
    if (this.appcuesContainer) {
      this.appcuesContainer.style.display = 'none';
    }
  }

  /**
   * Sends events to datadog directly as userActions to allow for funnel filtering.
   * Needed because segment does not send these events to Datadog.
   */
  #handleAllEvents(eventName: string, eventObject: Record<string, any>): void {
    this.datadog.addAction(`${eventName} (Appcues)`, eventObject);

    this.#handlePlanChange(eventName, eventObject);
  }

  /**
   * A very special function that handles plan change commands from Appcues flows
   *
   * Required:
   *
   * 1. Appcues title must have the correct formatting in the title.  Examples:
   *  - "Some example Title EmberChangePlan(plan:scale202506|interval:monthly|coupon:SCALE50OFF)"
   *  - "Some example Title EmberChangePlan(plan:scale202506|coupon:SCALE50OFF|interval:monthly)"
   *
   * 2. The interaction text must be one contained in the const ACCEPTABLE_INTERACTION_TEXTS_FOR_PLAN_CHANGE
   *
   * Example flows:
   * https://studio.appcues.com/flows/1e5973e3-a248-4421-ba8e-b69d8887c1ad/settings
   * https://studio.appcues.com/flows/2246f6b3-90d7-4c8f-8d17-5ff9013cf947/settings
   *
   *
   * Example event object:
   *  {
   *    "id": "step_interacted",
   *    "name": "Step Interacted",
   *    "flowId": "90b56faa-2cac-4ba2-a8e1-3b4948a381a0",
   *    "flowName": "Some example Title EmberChangePlan(plan:scale202506|coupon:SCALE50OFF|interval:monthly)",
   *    "flowType": "journey",
   *    "flowVersion": 1758325885423,
   *    "timestamp": 1758558580632,
   *    "sessionId": "8797ab7d-43b2-42aa-bbc0-47f23b1467e3",
   *    "localeName": "default",
   *    "localeId": "default",
   *    "stepId": "c3dda20c-754a-4f98-a122-f70cdc990b9b",
   *    "stepType": "modal",
   *    "stepNumber": 0,
   *    "stepChildId": "8cd932cc-c3f7-43fe-a130-e98f0bf9cc9c",
   *    "stepChildNumber": 1,
   *    "interactionType": "click",
   *    "interaction": {
   *        "category": "internal",
   *        "destination": "next",
   *        "text": "Confirm Upgrade"
   *    }
   *  }
   */
  async #handlePlanChange(eventName: string, eventObject: Record<string, any>): Promise<void> {
    const commandMatch = eventObject.flowName?.match(/EmberChangePlan\((.*?)\)/i);
    if (!commandMatch) return;

    const extractedParams: Record<string, string> = commandMatch[1]
      .split('|')
      .reduce((acc: Record<string, string>, param: string) => {
        const [key, value] = param.split(':');
        if (key && value) acc[key] = value;
        return acc;
      }, {});

    // Extract plan, interval, and coupon parameters
    const toPlan = extractedParams.plan;
    const toInterval = extractedParams.interval;
    const couponId = extractedParams.coupon;

    // Validate required parameters
    if (!toPlan) return;
    if (!toInterval || !['monthly', 'yearly'].includes(toInterval.toLowerCase())) return;

    // Only handle specific interaction events
    if (
      eventName == 'step_interacted' &&
      ACCEPTABLE_INTERACTION_TEXTS_FOR_PLAN_CHANGE.includes(eventObject.interaction.text)
    ) {
      try {
        await this.payment.changeToPlanFromAppcues({
          toPlan,
          toInterval: toInterval.toLowerCase() as 'monthly' | 'yearly',
          ...(couponId && { couponId })
        });
        this.alerts.success(this.intl.t('alerts.appcues_plan_change.success'));
      } catch (error) {
        this.alerts.warning(this.intl.t('alerts.appcues_plan_change.error'));
        this.errors.log('EmberChangePlan Failed to change plan', error);
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    appcues: AppcuesService;
  }
}
